<template>
  <v-container fluid>
    <SidebarNavigation />

    <v-card class="pa-1">
      <!-- 🔹 TOP BAR (Search + Action) -->
      <v-row class="pa-4">
        <v-col class="d-flex justify-end align-center">
          <!-- BUTTON -->
          <v-btn color="primary" class="mr-2" @click="openDialogCreate">
            <v-icon left>mdi-plus</v-icon>
            Tambah Data
          </v-btn>

          <!-- SEARCH -->
          <v-text-field
            v-model="pagination.search"
            label="Search"
            append-icon="mdi-magnify"
            dense
            hide-details
            outlined
            style="max-width: 250px"
            @input="debouncedSearch"
          />
        </v-col>
      </v-row>

      <!-- 🔹 TABLE -->
      <v-data-table
        mobile-breakpoint="0"
        fixed-header
        dense
        class="elevation-2 mx-4 mb-4"
        :headers="tableHeaders"
        :items="list"
        :items-per-page.sync="pagination.itemsPerPage"
        :page.sync="pagination.page"
        :sort-by="pagination.sortBy"
        :sort-desc="pagination.sortDesc"
        :server-items-length="total"
        item-key="id"
        @update:page="updateQuery('page', $event)"
        @update:items-per-page="updateQuery('itemsPerPage', $event)"
        @update:sort-by="updateQuery('sortBy', $event)"
        @update:sort-desc="updateQuery('sortDesc', $event)"
      >
        <template v-slot:body="{ items }">
          <tbody>
            <tr v-for="item in items" :key="item.id">
              <td class="text-center">{{ item.level_acc_1 }}</td>
              <td class="text-center">{{ item.level_acc_2 }}</td>
              <td class="text-center">{{ item.level_acc_3 }}</td>
              <td class="text-center">{{ item.level_acc_4 }}</td>
              <td class="text-center">{{ item.level_acc_5 }}</td>
              <td class="text-center">{{ item.account }}</td>
              <td class="text-center">{{ item.account_combination }}</td>
              <td class="text-center">{{ item.description }}</td>
              <td class="text-center">{{ item.corporate_name }}</td>

              <td class="text-center">
                <v-chip
                  small
                  :color="item.is_active == 1 ? 'green' : 'red'"
                  dark
                >
                  {{ item.is_active == 1 ? "Active" : "Inactive" }}
                </v-chip>
              </td>

              <td class="text-center">
                {{ item.created_by_name || "-" }}
              </td>

              <td class="text-center">
                <div
                  class="d-flex justify-center align-center"
                  style="gap: 6px"
                >
                  <!-- EDIT -->
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        icon
                        small
                        color="primary"
                        v-bind="attrs"
                        v-on="on"
                        @click="openDialogUpdate(item)"
                      >
                        <v-icon size="18">mdi-pencil</v-icon>
                      </v-btn>
                    </template>
                    <span>Edit</span>
                  </v-tooltip>

                  <!-- DELETE -->
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        icon
                        small
                        color="error"
                        v-bind="attrs"
                        v-on="on"
                        @click="openDialogDelete(item)"
                      >
                        <v-icon size="18">mdi-delete-outline</v-icon>
                      </v-btn>
                    </template>
                    <span>Hapus</span>
                  </v-tooltip>
                </div>
              </td>
            </tr>
          </tbody>
        </template>
      </v-data-table>
    </v-card>

    <!-- ================= CREATE DIALOG ================= -->
    <v-dialog
      v-model="isDialogOpenCreate"
      max-width="720"
      persistent
      scrollable
    >
      <v-card rounded="xl" elevation="8">
        <!-- HEADER -->
        <v-card-title
          class="d-flex align-center justify-space-between px-6 py-4"
        >
          <div class="d-flex align-center">
            <v-avatar color="primary" class="mr-3" size="40">
              <v-icon color="white">mdi-plus-box-outline</v-icon>
            </v-avatar>
            <div>
              <div class="text-h6 font-weight-bold">Tambah Data COA</div>
              <div class="text-caption text--secondary">
                Lengkapi informasi akun dengan benar
              </div>
            </div>
          </div>

          <v-btn
            icon
            small
            text
            :disabled="isSubmittingCreate"
            @click="isDialogOpenCreate = false"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-divider />

        <!-- BODY -->
        <v-card-text class="pa-6">
          <v-form v-model="isFormValidCreate" @submit.prevent="submitCreate">
            <v-row dense>
              <v-col cols="12" sm="6">
                <v-select
                  v-model="level_acc_1"
                  :items="listLevelAcc1"
                  label="Level Acc 1"
                  placeholder="Pilih Level Acc 1"
                  outlined
                  dense
                  prepend-inner-icon="mdi-shape-outline"
                  :rules="[(v) => !!v || 'Level Acc 1 wajib diisi']"
                />
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field
                  v-model="level_acc_2"
                  label="Level Acc 2"
                  placeholder="Masukkan Level Acc 2"
                  outlined
                  dense
                  prepend-inner-icon="mdi-format-list-numbered"
                  :rules="[(v) => !!v || 'Level Acc 2 wajib diisi']"
                />
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field
                  v-model="level_acc_3"
                  label="Level Acc 3"
                  placeholder="Masukkan Level Acc 3"
                  outlined
                  dense
                  prepend-inner-icon="mdi-format-list-numbered"
                  :rules="[(v) => !!v || 'Level Acc 3 wajib diisi']"
                />
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field
                  v-model="level_acc_4"
                  label="Level Acc 4"
                  placeholder="Masukkan Level Acc 4"
                  outlined
                  dense
                  prepend-inner-icon="mdi-format-list-numbered"
                  :rules="[(v) => !!v || 'Level Acc 4 wajib diisi']"
                />
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field
                  v-model="level_acc_5"
                  label="Level Acc 5"
                  placeholder="Masukkan Level Acc 5"
                  outlined
                  dense
                  prepend-inner-icon="mdi-format-list-numbered"
                  :rules="[(v) => !!v || 'Level Acc 5 wajib diisi']"
                />
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field
                  v-model="account"
                  label="Account"
                  placeholder="Masukkan Account"
                  outlined
                  dense
                  prepend-inner-icon="mdi-link-variant"
                  :rules="[(v) => !!v || 'Account wajib diisi']"
                />
              </v-col>

              <v-col cols="12" sm="6">
                <v-select
                  v-model="corporate_id"
                  :items="listCorporate"
                  label="Perusahaan"
                  placeholder="Pilih perusahaan"
                  outlined
                  dense
                  prepend-inner-icon="mdi-office-building-outline"
                  item-text="mst_corporate_name"
                  item-value="mst_corporate_id"
                  clearable
                  :rules="[(v) => !!v || 'Perusahaan wajib dipilih']"
                />
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field
                  v-model="account_combination"
                  label="Account Combination"
                  placeholder="42.07.B100.000.11101001.0000"
                  outlined
                  dense
                  prepend-inner-icon="mdi-database-outline"
                  maxlength="28"
                  persistent-hint
                  hint="Format: 42.07.B100.000.11101001.0000"
                  :rules="[
                    (v) => !!v || 'Account Combination wajib diisi',
                    (v) =>
                      /^\d{2}\.\d{2}\.[A-Z0-9]{4}\.\d{3}\.\d{8}\.\d{4}$/.test(
                        v,
                      ) || 'Format Account Combination tidak valid',
                  ]"
                  @input="formatCoaCombination"
                />
              </v-col>

              <v-col cols="12">
                <v-textarea
                  v-model="description"
                  label="Deskripsi"
                  placeholder="Masukkan deskripsi"
                  outlined
                  :rules="[(v) => !!v || 'Deskripsi wajib diisi']"
                  dense
                  prepend-inner-icon="mdi-text-box-outline"
                  auto-grow
                  rows="3"
                  counter="255"
                  maxlength="255"
                />
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>

        <v-divider />

        <!-- FOOTER -->
        <v-card-actions class="px-6 py-4">
          <v-spacer />
          <v-btn
            outlined
            color="secondary"
            :disabled="isSubmittingCreate"
            @click="isDialogOpenCreate = false"
          >
            Batal
          </v-btn>

          <v-btn
            color="primary"
            depressed
            :disabled="!isFormValidCreate"
            :loading="isSubmittingCreate"
            @click="submitCreate"
          >
            <v-icon left>mdi-content-save-outline</v-icon>
            Simpan
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ================= UPDATE DIALOG ================= -->
    <v-dialog v-model="isDialogOpenEdit" max-width="720" persistent scrollable>
      <v-card rounded="xl" elevation="8">
        <!-- HEADER -->
        <v-card-title
          class="d-flex align-center justify-space-between px-6 py-4"
        >
          <div class="d-flex align-center">
            <v-avatar color="primary" class="mr-3" size="40">
              <v-icon color="white">mdi-pencil-outline</v-icon>
            </v-avatar>
            <div>
              <div class="text-h6 font-weight-bold">Ubah Data COA</div>
              <div class="text-caption text--secondary">
                Perbarui informasi akun yang sudah ada
              </div>
            </div>
          </div>

          <v-btn
            icon
            small
            text
            :disabled="isSubmittingUpdate"
            @click="isDialogOpenEdit = false"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-divider />

        <!-- BODY -->
        <v-card-text class="pa-6">
          <v-form v-model="isFormValidUpdate" @submit.prevent="submitUpdate">
            <v-row dense>
              <v-col cols="12" md="6">
                <v-select
                  v-model="level_acc_1"
                  :items="listLevelAcc1"
                  label="Level Acc 1"
                  placeholder="Pilih Level Acc 1"
                  outlined
                  dense
                  prepend-inner-icon="mdi-shape-outline"
                  :rules="[(v) => !!v || 'Level Acc 1 wajib diisi']"
                />
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field
                  v-model="level_acc_2"
                  label="Level Acc 2"
                  placeholder="Masukkan Level Acc 2"
                  outlined
                  dense
                  prepend-inner-icon="mdi-format-list-numbered"
                  :rules="[(v) => !!v || 'Level Acc 2 wajib diisi']"
                />
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field
                  v-model="level_acc_3"
                  label="Level Acc 3"
                  placeholder="Masukkan Level Acc 3"
                  outlined
                  dense
                  prepend-inner-icon="mdi-format-list-numbered"
                  :rules="[(v) => !!v || 'Level Acc 3 wajib diisi']"
                />
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field
                  v-model="level_acc_4"
                  label="Level Acc 4"
                  placeholder="Masukkan Level Acc 4"
                  outlined
                  dense
                  prepend-inner-icon="mdi-format-list-numbered"
                  :rules="[(v) => !!v || 'Level Acc 4 wajib diisi']"
                />
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field
                  v-model="level_acc_5"
                  label="Level Acc 5"
                  placeholder="Masukkan Level Acc 5"
                  outlined
                  dense
                  prepend-inner-icon="mdi-format-list-numbered"
                  :rules="[(v) => !!v || 'Level Acc 5 wajib diisi']"
                />
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field
                  v-model="account"
                  label="Account"
                  placeholder="Masukkan Account"
                  outlined
                  dense
                  prepend-inner-icon="mdi-link-variant"
                  :rules="[(v) => !!v || 'Account wajib diisi']"
                />
              </v-col>

              <v-col cols="12" sm="6">
                <v-select
                  v-model="corporate_id"
                  :items="listCorporate"
                  label="Perusahaan"
                  placeholder="Pilih perusahaan"
                  outlined
                  dense
                  prepend-inner-icon="mdi-office-building-outline"
                  item-text="mst_corporate_name"
                  item-value="mst_corporate_id"
                  clearable
                  :rules="[(v) => !!v || 'Perusahaan wajib dipilih']"
                />
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field
                  v-model="account_combination"
                  label="Account Combination"
                  placeholder="42.07.B100.000.11101001.0000"
                  outlined
                  dense
                  prepend-inner-icon="mdi-database-outline"
                  maxlength="28"
                  persistent-hint
                  hint="Format: 42.07.B100.000.11101001.0000"
                  :rules="[
                    (v) => !!v || 'Account Combination wajib diisi',
                    (v) =>
                      /^\d{2}\.\d{2}\.[A-Z0-9]{4}\.\d{3}\.\d{8}\.\d{4}$/.test(
                        v,
                      ) || 'Format Account Combination tidak valid',
                  ]"
                  @input="formatCoaCombination"
                />
              </v-col>

              <v-col cols="12">
                <v-textarea
                  v-model="description"
                  label="Deskripsi"
                  placeholder="Masukkan deskripsi"
                  outlined
                  :rules="[(v) => !!v || 'Deskripsi wajib diisi']"
                  dense
                  prepend-inner-icon="mdi-text-box-outline"
                  auto-grow
                  rows="3"
                  counter="255"
                  maxlength="255"
                />
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>

        <v-divider />

        <!-- FOOTER -->
        <v-card-actions class="px-6 py-4">
          <v-spacer />
          <v-btn
            outlined
            color="secondary"
            :disabled="isSubmittingUpdate"
            @click="isDialogOpenEdit = false"
          >
            Batal
          </v-btn>

          <v-btn
            color="primary"
            depressed
            :disabled="!isFormValidUpdate"
            :loading="isSubmittingUpdate"
            @click="submitUpdate"
          >
            <v-icon left>mdi-content-save-edit-outline</v-icon>
            Update
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- DELETE -->
    <v-dialog v-model="isDialogOpenDelete" max-width="400px">
      <v-card>
        <v-card-title class="text-h6"> Konfirmasi Hapus </v-card-title>

        <v-card-text>
          Yakin ingin menghapus data <b>{{ account_combination }}</b
          >?
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>

          <v-btn color="red" dark @click="submitDelete"> Hapus </v-btn>

          <v-btn color="grey" text @click="isDialogOpenDelete = false">
            Batal
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <snackbarMessage />
  </v-container>
</template>

<script>
import api from "@/utils/api";
import SidebarNavigation from "@/components/SidebarNavigation.vue";
import snackbarMessage from "@/components/snackbarMessage.vue";
import store from "@/store";

export default {
  components: {
    SidebarNavigation,
    snackbarMessage,
  },
  data() {
    return {
      listLevelAcc1: [
        "ASET",
        "LIABILITAS",
        "EKUITAS",
        "PENDAPATAN",
        "BEBAN LANGSUNG",
        "BEBAN USAHA",
        "PENDAPATAN/BEBAN LAIN-LAIN",
        "LAIN-LAIN",
      ],

      tabItems: ["UOM"],
      isLoading: false,
      search: "",
      tab: 1,
      tableHeaders: [
        { text: "Level Acc 1", align: "center", value: "level_acc_1" },
        { text: "Level Acc 2", align: "center", value: "level_acc_2" },
        { text: "Level Acc 3", align: "center", value: "level_acc_3" },
        { text: "Level Acc 4", align: "center", value: "level_acc_4" },
        { text: "Level Acc 5", align: "center", value: "level_acc_5" },
        { text: "Account", align: "center", value: "account" },
        {
          text: "Account Combination",
          align: "center",
          value: "account_combination",
        },
        { text: "Deskripsi", align: "center", value: "description" },
        { text: "Perusahaan", align: "center", value: "corporate_name" },
        { text: "Status", align: "center", value: "is_active" },
        { text: "Dibuat Oleh", align: "center", value: "created_by_name" },
        { text: "Aksi", align: "center", sortable: false },
      ],
      pagination: {
        page: 1,
        itemsPerPage: 10,
        sortBy: "id",
        sortDesc: true,
        search: "",
      },
      total: null,
      list: [],
      listCorporate: [],

      isDialogOpenEdit: false,
      isDialogOpenDelete: false,
      isFormValidUpdate: false,
      isDialogOpenCreate: false,
      isFormValidCreate: false,
      selectedItem: null,
      id: null,
      level_acc_1: null,
      level_acc_2: null,
      level_acc_3: null,
      level_acc_4: null,
      level_acc_5: null,
      account: null,
      account_combination: null,
      description: null,
      corporate_id: null,
      isSubmittingCreate: false,
      isSubmittingUpdate: false,
    };
  },
  methods: {
    formatCoaCombination(value) {
      // Hapus semua titik dulu
      let raw = value.replace(/\./g, "");

      let result = "";

      const segments = [2, 2, 4, 3, 8, 4];
      let index = 0;

      for (let i = 0; i < segments.length; i++) {
        const seg = raw.slice(index, index + segments[i]);
        if (!seg) break;
        result += (i > 0 ? "." : "") + seg;
        index += segments[i];
      }

      this.account_combination = result;
    },
    fetch() {
      store.dispatch("showLoading");
      const query = this.$route.query;
      const params = {
        page: parseInt(query.page) || this.pagination.page,
        limit: parseInt(query.itemsPerPage) || this.pagination.itemsPerPage,
        search: query.search || this.pagination.search,
        sortBy: query.sortBy || this.pagination.sortBy || "name",
        sortDesc: query.sortDesc || this.pagination.sortDesc || false,
        startDate: query.startDate || this.startDate,
        endDate: query.endDate || this.endDate,
      };

      api
        .get("/coa", { params })
        .then(({ data }) => {
          this.list = data.data.data;
          this.total = data.data.total;
        })
        .catch((error) => {
          console.error("Error fetching parameters:", error);
        })
        .finally(() => {
          this.isLoading = false;
          store.dispatch("hideLoading");
        });
    },

    async fetchCorporate() {
      try {
        const { data } = await api.get("/corporates", {
          params: {
            category: "GROUP",
          },
        });

        this.listCorporate = (data?.data?.data || []).filter((e) =>
          e.mst_corporate_name?.toLowerCase().includes("fastana"),
        );
      } catch (error) {
        store.dispatch("triggerSnackbar", {
          message: error?.response?.data?.message || "Failed fetch corporate",
          isSuccess: false,
        });
      } finally {
        store.dispatch("hideLoading");
      }
    },
    openDialogCreate() {
      // reset semua field form
      this.name = null;

      // reset validasi form (biar tombol disable lagi)
      this.isFormValidCreate = false;

      // buka dialog
      this.isDialogOpenCreate = true;
    },
    openDialogUpdate(params = null) {
      this.selectedItem = params;
      this.id = params.id;
      this.level_acc_1 = params.level_acc_1;
      this.level_acc_2 = params.level_acc_2;
      this.level_acc_3 = params.level_acc_3;
      this.level_acc_4 = params.level_acc_4;
      this.level_acc_5 = params.level_acc_5;
      this.account = params.account;
      this.account_combination = params.account_combination;
      this.corporate_id = params.corporate_id;
      this.description = params.description;
      this.isDialogOpenEdit = true;
    },
    openDialogDelete(params = null) {
      this.selectedItem = params;
      this.id = params.id;
      this.account = params.account;
      this.corporate_id = params.corporate_id;
      this.account_combination = params.account_combination;
      this.isDialogOpenDelete = true;
    },
    submitCreate() {
      store.dispatch("showLoading");

      const payload = {
        level_acc_1: this.level_acc_1,
        level_acc_2: this.level_acc_2,
        level_acc_3: this.level_acc_3,
        level_acc_4: this.level_acc_4,
        level_acc_5: this.level_acc_5,
        account: this.account,
        corporate_id: this.corporate_id,
        account_combination: this.account_combination,
        description: this.description,
      };

      api
        .post("/coa", payload) // ⚠️ sesuaikan endpoint backend kamu
        .then((response) => {
          const message = response.data.message;

          this.fetch(); // refresh table
          this.isDialogOpenCreate = false;

          store.dispatch("triggerSnackbar", {
            message,
            isSuccess: true,
          });
        })
        .catch((error) => {
          let message = "Gagal menambahkan data!";

          if (error.response) {
            message = error.response.data.message || message;
          } else {
            message = error.message || message;
          }

          store.dispatch("triggerSnackbar", {
            message,
            isSuccess: false,
          });
        })
        .finally(() => {
          store.dispatch("hideLoading");
        });
    },
    submitUpdate() {
      store.dispatch("showLoading");
      const payload = {
        id: this.id,
        level_acc_1: this.level_acc_1,
        level_acc_2: this.level_acc_2,
        level_acc_3: this.level_acc_3,
        level_acc_4: this.level_acc_4,
        level_acc_5: this.level_acc_5,
        account: this.account,
        corporate_id: this.corporate_id,
        account_combination: this.account_combination,
        description: this.description,
      };
      api
        .post("/coa/update", payload)
        .then((response) => {
          const message = response.data.message;
          this.fetch();
          this.isDialogOpenEdit = false;
          store.dispatch("triggerSnackbar", { message, isSuccess: true });
        })
        .catch((error) => {
          let message = "Failed to save paramater!";
          if (error.response) {
            message = error.response.data.message || message; // Pesan error dari backend (jika ada)
          } else {
            message = error.message || message;
          }
          store.dispatch("triggerSnackbar", {
            message,
            isSuccess: false,
          });
        })
        .finally(() => {
          this.isLoading = false;
          store.dispatch("hideLoading");
        });
    },

    submitDelete() {
      store.dispatch("showLoading");

      api
        .post("/coa/delete", {
          id: this.id,
        }) // ⚠️ sesuaikan endpoint backend
        .then((response) => {
          const message = response.data.message;

          this.fetch();
          this.isDialogOpenDelete = false;

          store.dispatch("triggerSnackbar", {
            message,
            isSuccess: true,
          });
        })
        .catch((error) => {
          let message = "Gagal menghapus data!";

          if (error.response) {
            message = error.response.data.message || message;
          } else {
            message = error.message || message;
          }

          store.dispatch("triggerSnackbar", {
            message,
            isSuccess: false,
          });
        })
        .finally(() => {
          store.dispatch("hideLoading");
        });
    },

    debouncedSearch() {
      this.updateQuery("search", this.pagination.search);
    },
    updatePagination(query) {
      // console.log(query);
      this.pagination.page = parseInt(query.page) || 1;
      this.pagination.itemsPerPage = parseInt(query.itemsPerPage) || 10;
      this.pagination.sortBy = query.sortBy || "id";
      this.pagination.sortDesc = query.sortDesc === "true" ? true : false;
      this.pagination.search = query.search || this.pagination.search || "";
      this.selectedDivisionId = parseInt(query.divisionId) || 0;
      this.selectedPositionId = parseInt(query.positionId) || 0;
      this.selectedSiteId = parseInt(query.siteId) || 0;
    },
    updateQuery(key, value) {
      if (this.$route.query[key] !== value) {
        this.$router
          .push({
            query: {
              ...this.$route.query,
              [key]: value, // Mengupdate parameter query dengan key dan value
            },
          })
          .catch((err) => {
            // Handle error jika ada (contohnya, karena navigasi yang redundant)
            if (err.name !== "NavigationDuplicated") {
              console.error(err);
            }
          });
      }
      this.updatePagination(this.$route.query);
    },
  },
  mounted() {
    store.commit("setTitle", "COA");
    this.fetchCorporate();
    this.updatePagination(this.$route.query);
  },
  watch: {
    pagination: {
      handler: "fetch",
      deep: true, // Memastikan perubahan dalam objek pagination terdeteksi
    },
  },
};
</script>

<style scoped>
.v-data-table td:nth-child(2),
td:nth-child(4),
td:nth-child(5) {
  text-align: center;
}
</style>
